import { PlayCircle } from 'lucide-react';
import Reveal from './ui/Reveal';
import PlayableVideo from './ui/PlayableVideo';
import { cn } from '../lib/cn';
import { useMediaSlot } from '../lib/MediaContext';

interface VideoBlockProps {
  title: string;
  description?: string;
  label: string;
  slotKey: string;
  tone?: 'dark' | 'paper' | 'sand';
  size?: 'md' | 'lg';
}

const toneClasses = {
  dark: { section: 'bg-ink', title: 'text-paper', desc: 'text-dark-muted' },
  paper: { section: 'bg-paper', title: 'text-ink', desc: 'text-muted' },
  sand: { section: 'bg-sand', title: 'text-ink', desc: 'text-muted' },
};

export default function VideoBlock({ title, description, label, slotKey, tone = 'dark', size = 'md' }: VideoBlockProps) {
  const slot = useMediaSlot(slotKey);
  const t = toneClasses[tone];
  const padding = size === 'lg' ? 'py-20 md:py-24' : 'py-14 md:py-16';
  const videoHeight = size === 'lg' ? 'h-[280px] md:h-[400px]' : 'h-[240px] md:h-[320px]';

  return (
    <section className={cn(t.section, padding, 'px-8')}>
      <Reveal className="max-w-[900px] mx-auto text-center">
        <h2 className={cn('font-serif font-medium text-2xl md:text-[28px] m-0 mb-3.5', t.title)}>{title}</h2>
        {description && <p className={cn('text-[15px] m-0 mb-8', t.desc)}>{description}</p>}
        {slot?.file_path ? (
          <PlayableVideo src={slot.file_path} title={label} className={cn('w-full rounded-lg bg-black border-0', videoHeight)} />
        ) : (
          <button
            type="button"
            aria-label={`Play video — ${label}`}
            className={cn(
              'group relative w-full rounded-lg flex items-center justify-center gap-3 cursor-pointer border transition-transform duration-250 hover:-translate-y-1',
              videoHeight,
            )}
            style={{ background: '#2C2A24', borderColor: '#3A362E' }}
          >
            <PlayCircle
              size={56}
              strokeWidth={1.3}
              className="text-dark-muted transition-transform duration-250 group-hover:scale-110 group-hover:text-gold"
            />
            <span className="sr-only">{label}</span>
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[13px] text-dark-muted tracking-[0.02em]">
              {label}
            </span>
          </button>
        )}
      </Reveal>
    </section>
  );
}
